import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  GoogleAuthProvider,
  signInWithPopup,
  onAuthStateChanged,
  signOut,
  setPersistence,
  browserLocalPersistence
} from 'firebase/auth';
import {
  doc,
  getDoc,
  setDoc,
  getFirestore
} from 'firebase/firestore';
import { auth } from '../config/firebase';
import { Usuario } from '../types';

const db = getFirestore();

interface AuthContextType {
  usuario: Usuario | null;
  loading: boolean;
  error: string | null;
  signInWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [usuario, setUsuario] = useState<Usuario | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const getOrCreateUsuario = async (firebaseUser: any): Promise<Usuario> => {
    const userRef = doc(db, 'users', firebaseUser.uid);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
      const data = userDoc.data();
      return {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: data.displayName || firebaseUser.displayName,
        photoUrl: data.photoUrl || firebaseUser.photoURL,
        role: data.role || 'LECTOR',
        obrasAsignadas: data.obrasAsignadas || [],
        puedeVerTodasLasObras: data.puedeVerTodasLasObras || false,
        ...data
      } as Usuario;
    } else {
      const newUser: Usuario = {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: firebaseUser.displayName,
        photoUrl: firebaseUser.photoURL,
        role: firebaseUser.email === 'brunoboggio@gmail.com' ? 'ADMIN' : 'LECTOR',
        obrasAsignadas: [],
        puedeVerTodasLasObras: false,
        createdAt: new Date(),
        emailVerified: firebaseUser.emailVerified
      };
      await setDoc(userRef, newUser);
      return newUser;
    }
  };

  useEffect(() => {
    setPersistence(auth, browserLocalPersistence)
      .catch(() => {
        setError('Error al configurar persistencia');
      });

    const unsubscribe = onAuthStateChanged(auth,
      async (user) => {
        try {
          if (user) {
            const userData = await getOrCreateUsuario(user);
            setUsuario(userData);
          } else {
            setUsuario(null);
          }
        } catch (err) {
          console.error(err);
          setError('Error al obtener datos del usuario');
        } finally {
          setLoading(false);
        }
      },
      () => {
        setError('Error al verificar el estado de autenticación');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const signInWithGoogle = async () => {
    try {
      setError(null);
      setLoading(true);
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({
        prompt: 'select_account'
      });

      const result = await signInWithPopup(auth, provider);

      if (result.user) {
        try {
          const userData = await getOrCreateUsuario(result.user);
          setUsuario(userData);
        } catch (err: any) {
          setError('Error al procesar datos de usuario: ' + err.message);
          throw err;
        }
      }
    } catch (err: any) {
      const errorMessage = err.code === 'auth/popup-closed-by-user'
        ? 'Ventana de inicio de sesión cerrada. Por favor, intente nuevamente.'
        : err.message || 'Error al iniciar sesión con Google';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      setError(null);
      setLoading(true);
      await signOut(auth);
      setUsuario(null);
    } catch (err: any) {
      setError(err.message || 'Error al cerrar sesión');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    usuario,
    loading,
    error,
    signInWithGoogle,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};